import { useState, type ReactNode, type Ref } from 'react';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Spinner,
} from '@monksflow/monks-ui';
import { CheckIcon, ChevronDownIcon, Eye, EyeOff } from 'lucide-react';
import { CommentsPanelSkeleton } from './CommentsPanelSkeleton';
import { useTranslation } from 'react-i18next';

const SORT_LABEL_KEY = {
  desc: 'commentOnFile.commentsPanel.sortNewest',
  asc: 'commentOnFile.commentsPanel.sortOldest',
} as const satisfies Record<'asc' | 'desc', string>;
const SORT_OPTIONS: ReadonlyArray<'desc' | 'asc'> = ['desc', 'asc'];

export interface CommentsPanelProps {
  isLoading: boolean;
  /** Resolved-comments visibility toggle (the eye button). */
  showResolved: boolean;
  onToggleResolved: () => void;
  /** Any comments at all, INCLUDING resolved ones hidden by the filter. Gates the
      toolbar so it shows whenever the resolved toggle is meaningful (i.e. there
      are comments to reveal) but disappears on a truly-empty thread. */
  hasComments: boolean;
  /** Root-comment sort order + handler; the sort control hides when not sortable. */
  sortOrder: 'asc' | 'desc';
  onSortChange: (order: 'asc' | 'desc') => void;
  sortable: boolean;
  /** Empty-state copy — owned by the caller so briefs and files can word it for their surface. */
  isEmpty: boolean;
  emptyTitle: string;
  emptyDescription: ReactNode;
  /** Infinite-scroll "load more" affordance. */
  hasMore: boolean;
  isFetchingMore: boolean;
  onLoadMore: () => void;
  /** Scroll container ref — used by the file panel to scroll a linked comment into view. */
  scrollRef?: Ref<HTMLDivElement>;
  /** The mapped comment threads. */
  children: ReactNode;
  /** The composer pinned below the list (a full-width separator divides it from the threads). */
  footer: ReactNode;
}

/**
 * The shared comment-panel body — the toolbar (resolved toggle + sort), the
 * scrollable thread list with its empty/loading/load-more states, and the
 * composer footer. Rendered identically by the brief panel and the asset panel
 * so the two surfaces stay visually in lockstep; everything that legitimately
 * differs (the thread component, the composer, the empty copy, the scroll
 * mechanism) is injected by the caller. The card "slot" inside each thread is
 * the separately-shared `CommentCardSurface`.
 */
export function CommentsPanel(props: CommentsPanelProps) {
  const { t } = useTranslation('projects');

  const [sortMenuOpen, setSortMenuOpen] = useState(false);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {props.isLoading && (
        // Mirrors the resolved-toggle / sort row below so the list doesn't
        // jump up when the real toolbar swaps in.
        <div className="flex shrink-0 items-center justify-between gap-2 px-4 pt-2">
          <Skeleton className="h-7 w-40 rounded-md" />
          <Skeleton className="h-7 w-20 rounded-md" />
        </div>
      )}

      {/* Shown whenever any comments exist (incl. resolved hidden by the filter)
          so the resolved toggle is never unreachable when all are resolved, yet
          stays hidden on a truly-empty thread. */}
      {!props.isLoading && props.hasComments && (
        <div className="flex shrink-0 items-center justify-between gap-2 px-4 pt-2">
          <Button
            variant="ghost"
            size="mini"
            aria-pressed={props.showResolved}
            aria-label={
              props.showResolved
                ? t('commentOnFile.commentsPanel.hideResolvedComments')
                : t('commentOnFile.commentsPanel.showResolvedComments')
            }
            onClick={props.onToggleResolved}
          >
            {props.showResolved ? (
              <Eye className="size-4" />
            ) : (
              <EyeOff className="size-4 text-muted-foreground" />
            )}
            {t('commentOnFile.commentsPanel.resolvedComments')}
          </Button>
          {props.sortable && (
            <Popover open={sortMenuOpen} onOpenChange={setSortMenuOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="mini"
                  aria-label={t('commentOnFile.commentsPanel.sortRootComments')}
                >
                  {t(SORT_LABEL_KEY[props.sortOrder])}
                  <ChevronDownIcon className="size-4 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-48 p-1">
                <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  {t('commentOnFile.commentsPanel.sortBy')}
                </div>
                {SORT_OPTIONS.map((order) => (
                  <Button
                    key={order}
                    variant="ghost"
                    size="mini"
                    className="w-full justify-between"
                    onClick={() => {
                      props.onSortChange(order);
                      setSortMenuOpen(false);
                    }}
                  >
                    {t(SORT_LABEL_KEY[order])}
                    {props.sortOrder === order && (
                      <CheckIcon className="size-4 text-muted-foreground" />
                    )}
                  </Button>
                ))}
              </PopoverContent>
            </Popover>
          )}
        </div>
      )}

      {/* 16px left / 10px right (Figma node 8321-106420); the smaller right +
          scrollbar gutter realigns content to the toolbar's `px-4`. */}
      <div ref={props.scrollRef} className="flex-1 overflow-x-hidden overflow-y-auto pl-4 pr-2.5">
        {props.isLoading && <CommentsPanelSkeleton />}

        {!props.isLoading && props.isEmpty && (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <h3 className="text-heading-xs font-medium leading-6 text-foreground">
              {props.emptyTitle}
            </h3>
            <p className="text-body-sm leading-6 text-foreground-alt">{props.emptyDescription}</p>
          </div>
        )}

        {!props.isLoading && props.children}

        {props.hasMore && (
          <div className="flex justify-center py-3">
            <Button
              variant="ghost"
              size="sm"
              disabled={props.isFetchingMore}
              onClick={props.onLoadMore}
            >
              {props.isFetchingMore ? (
                <Spinner />
              ) : (
                t('commentOnFile.commentsPanel.loadMoreComments')
              )}
            </Button>
          </div>
        )}
      </div>

      {props.footer && <div className="border-t border-border-light p-4">{props.footer}</div>}
    </div>
  );
}
